import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/i18n";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Search, MapPin, ArrowRight } from "lucide-react";

const FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='hsl(36,28%,90%)'/><text x='50%' y='50%' font-size='40' fill='hsl(38,52%,46%)' text-anchor='middle' font-family='serif'>神明</text></svg>`
);

export default function Deity() {
  const { t, lang, pick } = useI18n();
  const navigate = useNavigate();
  const [deities, setDeities] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback((query) => {
    api.get("/deities", { params: query ? { q: query } : {} }).then((r) => setDeities(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(""); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--brand-bronze))] font-semibold">PROTECTION</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("nav.deity")}</h1>
        <p className="mt-2 text-muted-foreground">{lang === "zh" ? "诚心敬拜，神明护佑。点选神明查看简介与地点地图。" : "Choose a deity to view details and location map."}</p>
      </div>

      <form className="mb-6 flex gap-2 max-w-md" onSubmit={(e) => { e.preventDefault(); load(q); }}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("deity.searchPlaceholder")} data-testid="deity-search-input" />
        <Button type="submit" className="bg-[hsl(var(--primary))] text-primary-foreground" data-testid="deity-search-button"><Search className="h-4 w-4" /></Button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {deities.map((d) => (
          <Card key={d.id} className="card-lift cursor-pointer border-border overflow-hidden" onClick={() => navigate(`/deity/${d.id}`)} data-testid={`deity-card-${d.id}`}>
            <AspectRatio ratio={4 / 3}>
              <img src={d.image_url || FALLBACK} onError={(e) => { e.currentTarget.src = FALLBACK; }} alt={pick(d, "name")} className="h-full w-full object-cover" />
            </AspectRatio>
            <CardContent className="p-4">
              <h3 className="font-display text-lg font-semibold">{pick(d, "name")}</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2 leading-6">{pick(d, "desc")}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{(d.locations?.length || (d.address ? 1 : 0))} {t("deityx.locations")}</span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--brand-vermillion))]">{t("deityx.viewDetail")} <ArrowRight className="h-4 w-4" /></span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
